import React from 'react';
import { useTranslation } from 'react-i18next';

import { LabelParameters, LabelType } from '../../services/types';

import { BinSearch } from './BinSearch';

interface LabelParameterFormProps {
  labelType: LabelType;
  parameters: LabelParameters;
  onParametersChange: (parameters: LabelParameters) => void;
  onBlur?: () => void;
}

type InputConfig = {
  key: string;
  label: string;
  type?: 'text' | 'number';
  required?: boolean;
  placeholder?: string;
};

export const LabelParameterForm: React.FC<LabelParameterFormProps> = ({
  labelType,
  parameters,
  onParametersChange,
  onBlur,
}) => {
  const { t } = useTranslation();

  const getFieldsForLabelType = (): InputConfig[] => {
    switch (labelType) {
      case LabelType.INTERNAL_PART:
        return [
          {
            key: 'partNumber',
            label: t('labelPrinting.fields.partNumber', {
              defaultValue: 'Epicor PartNum',
            }),
            required: true,
            placeholder: 'e.g., 123456',
          },
        ];
      case LabelType.EXTERNAL_PART:
        return [
          {
            key: 'part',
            label: t('labelPrinting.fields.part', {
              defaultValue: 'Part',
            }),
            required: true,
            placeholder: 'e.g., UMS-00000123456',
          },
        ];
      case LabelType.SHIPPING:
        return [
          {
            key: 'id',
            label: t('labelPrinting.fields.shippingId', {
              defaultValue: 'Customer ID / Ship To ID',
            }),
            required: true,
            placeholder: 'e.g., 12345',
          },
        ];
      case LabelType.BIN:
        return [
          {
            key: 'description',
            label: t('labelPrinting.fields.binDescription', {
              defaultValue: 'Bin Description',
            }),
            required: true,
            placeholder: 'e.g., E2-03-07',
          },
        ];
      default:
        return [];
    }
  };

  const fields: InputConfig[] = getFieldsForLabelType();

  const handleChange = (key: string, value: string | number) => {
    onParametersChange({
      ...parameters,
      [key]: value,
    } as LabelParameters);
  };

  return (
    <div className="space-y-4">
      {fields.map((field) => (
        <div key={field.key}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          {labelType === LabelType.BIN && field.key === 'description' ? (
            <BinSearch
              value={
                ((parameters as Record<string, string | number>)[
                  field.key
                ] as string) || ''
              }
              onChange={(value) => handleChange(field.key, value)}
              onBlur={onBlur}
              placeholder={field.placeholder}
              required={field.required}
            />
          ) : (
            <input
              type={field.type || 'text'}
              value={
                (parameters as Record<string, string | number>)[field.key] || ''
              }
              onChange={(e) =>
                handleChange(
                  field.key,
                  field.type === 'number'
                    ? parseFloat(e.target.value)
                    : e.target.value
                )
              }
              onBlur={onBlur}
              placeholder={field.placeholder}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          )}
        </div>
      ))}
    </div>
  );
};
